import { useApolloClient } from '@apollo/client';
import type {
  ApplicantCount,
  JobDisplayTag,
} from '@seek/ca-graphql-schema/types';
import type { JobTagType } from '@seek/chalice-types';
import { useHubble } from '@seek/hubble';
import { mapJobTagsToBadges } from '@seek/seek-jobs-analytics';
import { useCallback } from 'react';

import { useSplitViewContext } from 'src/handlers/SplitViewContext';
import {
  getIsAddSalaryPrefNudgeShown,
  getIsSalaryMatch,
} from 'src/hooks/trackJobDetailsLoaded';
import { useSolHash } from 'src/hooks/useSolHash';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import {
  useEnableNumberOfApplicants,
  useIsHomeFeedAPIEnabled,
  useIsHomepageRecsBadgingStrongApplicantEnabled,
} from 'src/store/featureFlags/hooks';
import type {
  JobDetails,
  PersonalisedJobDetails,
} from 'src/store/jobdetails/types';
import { useSelector } from 'src/store/react';
import {
  selectAuthenticated,
  selectLocation,
  selectSearchQuery,
  selectSolMetadata,
} from 'src/store/selectors';
import type { ApolloClientType } from 'src/types/apollo';
import { getViewJobOriginRef } from 'src/utils/eventCapture/eventCaptureUtils';
import { getRecommendedBadgesForTracking } from 'src/utils/getRecommendedJobAnalyticsBadges';
import { mapDisplayTagsType } from 'src/utils/mapJobTagsType';
import { useMelwaysLocale } from 'src/utils/melwaysHelper';

import { useSourcrRecruiterForTracking } from '../SearchResultPage/SearchResultList/JobListItem/utils/utils';

interface Options {
  jobDetails: JobDetails;
  isHirerVerified: boolean;
  jobDetailsPersonalised?: PersonalisedJobDetails;
  solMetadataReference?: string;
  isMatchedSkillsWidgetShown?: boolean;
  isMatchedSkillsNudgeShown?: boolean;
  jobOrigin?: string;
}

type UseApplyTracking = (options: Options) => [string, () => void];

const useJobBadgesTracking = (
  jobId: string,
  jobOrigin: string | undefined,
): JobTagType[] => {
  const selectedJob = useSplitViewContext()?.selectedJob;
  const apolloClient = useApolloClient() as ApolloClientType;
  const visitorId = useHubble().visitorId();
  const location = useSelector(selectLocation);
  const locale = useMelwaysLocale();
  const isHomepageRecsBadgingStrongApplicantEnabled =
    useIsHomepageRecsBadgingStrongApplicantEnabled();
  const isFeedEnabled = useIsHomeFeedAPIEnabled();

  /**
   * Only applicable if user is in splitView since currently
   * there is no way to track jobTags if user is in standalone
   * job details page.
   */
  if (selectedJob) {
    return mapJobTagsToBadges(selectedJob.tags);
  }

  return getRecommendedBadgesForTracking({
    apolloClient,
    locale,
    jobId,
    visitorId,
    origin: location.query?.ref || jobOrigin,
    features: {
      isHomepageRecsBadgingStrongApplicantEnabled,
      isFeedEnabled,
    },
  });
};

const useJdvBadgesTracking = ({
  hasTopApplicantBadge,
  displayTags,
}: {
  hasTopApplicantBadge: boolean;
  displayTags?: JobDisplayTag[];
}) => {
  const topApplicantBadge: Array<'TOP_APPLICANT'> = hasTopApplicantBadge
    ? ['TOP_APPLICANT']
    : [];
  return [...topApplicantBadge, ...mapDisplayTagsType(displayTags)];
};

const useSourcrOnJobCardTracking = () => {
  // Sourcr recruiter only appears on SplitView job cards currently
  const selectedJob = useSplitViewContext()?.selectedJob;

  return useSourcrRecruiterForTracking(selectedJob);
};

export const useApplyTracking: UseApplyTracking = ({
  jobDetails,
  jobDetailsPersonalised,
  solMetadataReference,
  isHirerVerified,
  isMatchedSkillsWidgetShown,
  isMatchedSkillsNudgeShown,
  jobOrigin,
}) => {
  const analyticsFacade = useAnalyticsFacade();
  const isAuthenticated = useSelector(selectAuthenticated);
  const solMetadata = useSelector(selectSolMetadata);
  const searchParams = useSelector(selectSearchQuery);

  const { sourcr } = jobDetails;
  const { id, isLinkOut, salary, products } = jobDetails?.job || {};
  const [urlStr, solMeta] = useSolHash({
    id,
    solMetadataReference,
  });

  const salaryMatch = jobDetailsPersonalised?.salaryMatch;
  const isSalaryMatch = getIsSalaryMatch(salaryMatch);
  const searchRemoteOptions = searchParams?.workarrangement;
  const isAddSalaryPrefNudgeShown = getIsAddSalaryPrefNudgeShown({
    isAuthenticated,
    isSalaryLabel: Boolean(salary?.label),
    salaryMatch,
  });

  const isNumberOfApplicantsEnabled =
    useEnableNumberOfApplicants() && isAuthenticated;

  const jobTags = useJobBadgesTracking(id, jobOrigin);
  const jdvDisplayTags = useJdvBadgesTracking({
    hasTopApplicantBadge: Boolean(jobDetailsPersonalised?.topApplicantBadge),
    displayTags: jobDetails?.job?.products?.displayTags,
  });
  const sourcrRecruiterOnJobCard = useSourcrOnJobCardTracking();

  const experimentFlightId = solMetadata?.tags?.mordor__flights ?? '';

  const onApplyClick = useCallback(() => {
    /**
     * If there is no ref query in the URL and the user is
     * in splitView then it is guaranteed the user clicked
     * apply from split view SERP.
     */
    const { count: jobApplicantsVolume } = isNumberOfApplicantsEnabled
      ? (jobDetails?.insights?.[0] as ApplicantCount)
      : {};

    const viewJobOriginRef = getViewJobOriginRef();
    const ref = jobOrigin || viewJobOriginRef || 'search-split';

    analyticsFacade.jobApplyClicked(
      {
        experimentFlightId,
        href: urlStr,
        jobBadges: jobTags,
        jobBadgesShown: jdvDisplayTags,
        jobApplicantsVolume,
        jobViewOriginQuery: ref,
        isHirerVerified,
        isMatchedSkillsWidgetShown,
        isMatchedSkillsNudgeShown,
        jobBadgesFilter: searchParams?.tags ? [searchParams?.tags] : [],
        jobCardSellPointCount: products?.bullets?.length,
        hasSourcr: Boolean(sourcr),
      },
      {
        isAddSalaryPrefNudgeShown,
        isSalaryMatch,
        searchRemoteOptions,
        jobBadges: jobTags,
        jobBadgesShown: jdvDisplayTags,
        jobId: id,
        linkout: isLinkOut,
        solMetadata: solMeta,
        thirdPartyLink: isLinkOut,
        isMatchedSkillsWidgetShown,
        isMatchedSkillsNudgeShown,
        ...(sourcrRecruiterOnJobCard
          ? { recruiterId: sourcrRecruiterOnJobCard.id }
          : {}),
      },
    );
  }, [
    jobOrigin,
    analyticsFacade,
    experimentFlightId,
    urlStr,
    isHirerVerified,
    isMatchedSkillsWidgetShown,
    isMatchedSkillsNudgeShown,
    isAddSalaryPrefNudgeShown,
    isSalaryMatch,
    searchRemoteOptions,
    id,
    isLinkOut,
    solMeta,
    jobTags,
    jdvDisplayTags,
    searchParams?.tags,
    products?.bullets,
    sourcr,
    sourcrRecruiterOnJobCard,
    isNumberOfApplicantsEnabled,
    jobDetails.insights,
  ]);

  return [urlStr, onApplyClick];
};
